import { useState } from "react";
import { Route, Routes } from "react-router-dom";

import { MobileHeading } from "features/layout/components";
import { ManageSubscriptionDialog } from "features/subscriptions/components";
import { PRICING_PLANS } from "features/subscriptions/constants";

import RenderIf from "common/components/RenderIf";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import { getOutreachLicenseCount } from "common/helpers/workspaceLicenseCounts";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";

import AccountAvatarsTooltip from "../AccountAvatarsTooltip";
import ActivatePlanLayout from "../ActivatePlanLayout";
import AgencySubscriptionCard from "../AgencySubscriptionCard";
import Invoices from "../Invoices";
import PlanInvoicesToggle from "../PlanInvoicesToggle";
import PlanStatCard from "../PlanStatCard";
import SettingsScrollContent from "../SettingsScrollContent";
import AllocateLtdSeats from "./AllocateLtdSeats";
import LifetimeDealCard from "./LifetimeDealCard";
import ManageAgencySeats from "./ManageAgencySeats";
import OutreachPlanCard from "./OutreachPlanCard";

export default function User() {
  const [isManageOpen, setIsManageOpen] = useState(false);

  const selectedWorkspace = useSelectedWorkspaceContext();
  const {
    id: workspaceId,
    subscription_status,
    role,
    agency_subscription_status: agencySubscriptionStatus,
    lifetime_license_count,
    agency_license_count: agencyLicenseCount,
  } = selectedWorkspace;
  const outreachSeatCount = getOutreachLicenseCount(selectedWorkspace);

  // Seat priority: LTD > Agency > Outreach; outreach count is 0 for agency workspaces.
  const seatCount =
    lifetime_license_count || agencyLicenseCount || outreachSeatCount;
  const { workspace } = useWorkspaceDetails(workspaceId);
  const { agencyPlan, appSumoPromotion, ltdPromotion } = usePromotions();
  const { outreachLicensedAccounts } = useLicensedAccounts(workspaceId);

  const isLifetimeSuspended =
    (ltdPromotion ?? appSumoPromotion)?.suspended ?? false;
  const isOwner = role === "owner";
  const isOwnerOrAdmin = isOwner || role === "admin";
  const hasActiveOutreachPlan =
    subscription_status && subscription_status !== "cancelled";
  const hasActiveAgencyPlan =
    agencySubscriptionStatus && agencySubscriptionStatus !== "cancelled";

  let mainCard = (
    <ActivatePlanLayout plan="user" onActivate={() => setIsManageOpen(true)} />
  );
  const actionComponent = hasActiveOutreachPlan && (
    <PlanInvoicesToggle planLabel={PRICING_PLANS.user.name} />
  );

  // LTD takes priority: workspaces with lifetime seats can't activate Outreach
  if (lifetime_license_count) {
    mainCard = <LifetimeDealCard />;
    // Show the Agency card if the workspace has an agency subscription
  } else if (hasActiveAgencyPlan) {
    mainCard = <AgencySubscriptionCard isAgencyPage={false} />;
  } else if (hasActiveOutreachPlan) {
    mainCard = (
      <OutreachPlanCard openManageSubscription={() => setIsManageOpen(true)} />
    );
  }

  const mainContent = (
    <>
      {mainCard}
      {/*Seats/Assigned stats are only meaningful when there's an active plan to allocate seats for*/}
      <RenderIf
        condition={
          !!lifetime_license_count ||
          hasActiveAgencyPlan ||
          hasActiveOutreachPlan
        }
      >
        <section className="grid lg:grid-cols-2 grid-cols-1 gap-1.5">
          <PlanStatCard
            title="Seats"
            action={
              <>
                <RenderIf
                  condition={
                    isOwner && lifetime_license_count && !isLifetimeSuspended
                  }
                >
                  <AllocateLtdSeats />
                </RenderIf>

                {/* Show inline agency seat allocation if agency plan is active */}
                <RenderIf
                  condition={
                    isOwnerOrAdmin &&
                    agencyPlan &&
                    agencyPlan.subscription.status === "active"
                  }
                >
                  <ManageAgencySeats />
                </RenderIf>
              </>
            }
          >
            <Tag variant="tertiary" size="md" className="w-fit">
              {seatCount}
            </Tag>
          </PlanStatCard>

          <PlanStatCard title="Assigned">
            <Tag variant="tertiary" size="md" className="w-fit">
              {outreachLicensedAccounts.length}
              <span className="text-black-500">/ {seatCount}</span>
            </Tag>

            {/* Assigned & pending accounts */}
            <RenderIf condition={seatCount > 0}>
              <AccountAvatarsTooltip
                accounts={outreachLicensedAccounts}
                totalCount={seatCount}
              />
            </RenderIf>
          </PlanStatCard>
        </section>
      </RenderIf>
    </>
  );

  return (
    <>
      <SettingsScrollContent desktopActionComponent={actionComponent}>
        {actionComponent && <MobileHeading actionComponent={actionComponent} />}
        <Routes>
          <Route path="" element={mainContent} />
          <Route path="invoices" element={<Invoices type="user" />} />
        </Routes>
      </SettingsScrollContent>

      <ManageSubscriptionDialog
        initialState={
          workspace?.subscription
            ? {
                subscription: workspace.subscription,
                seats:
                  getOutreachLicenseCount(workspace) ?? outreachSeatCount ?? 1,
              }
            : "user"
        }
        isOpen={isManageOpen}
        setIsOpen={setIsManageOpen}
        mixpanelSource="billing_page"
      />
    </>
  );
}
